import { Injectable, signal } from '@angular/core';
import { getTimeOffset } from '../utils/date-format';
import { SessionStorageService } from './session-storage.service';

export interface ITimeZone {
    label: string;
    value: number;
    timezone: string;
    tooltip: string;
}

@Injectable({
    providedIn: 'root'
})
export class TimeZoneService {
    // Current time zone offset (in hours)
    currentTimeZone = signal<number>(getTimeOffset());

    constructor(
        private sessionStorageService: SessionStorageService,
    ) {}

    /**
     * Initializes the current time zone from session storage or uses the default offset.
     */
    timeZoneInit(): void {
        const timeZone = this.sessionStorageService.getItem('time-zone');
        this.currentTimeZone.set(timeZone ? Number(timeZone) : getTimeOffset());
    }

    /**
     * Adjusts the date by a given hour offset using UTC hours.
     * @param date - The date to adjust (Date or string)
     * @param hourAdjustment - Number of hours to adjust
     * @returns Adjusted Date object
     */
    private adjustDateHours(date: Date | string, hourAdjustment: number): Date {
        const baseDate = new Date(date);
        baseDate.setUTCHours(baseDate.getUTCHours() + hourAdjustment);
        return baseDate;
    }

    /**
     * Converts the given date to the current time zone.
     * @param date - The input date (Date or string)
     * @returns Date adjusted to the current time zone
     */
    setCurrentTimeZoneDate(date: Date | string): Date {
        const offsetDiff = this.currentTimeZone() - getTimeOffset();
        return this.adjustDateHours(date, offsetDiff);
    }

    /**
     * Converts the given date to the current time zone for a range (difference between selected and local time zone).
     * @param date - The input date (Date or string)
     * @returns Date adjusted for the current time zone range
     */
    setCurrentTimeZoneDateForRange(date: Date | string): Date {
        const offsetDiff = this.currentTimeZone() - getTimeOffset();
        return this.adjustDateHours(date, offsetDiff);
    }

    /**
     * Converts the given date from the current time zone back to the local time zone (for saving).
     * @param date - The input date (Date or string)
     * @returns Date adjusted for saving
     */
    setCurrentTimeZoneDateSave(date: Date | string): Date {
        const offsetDiff = getTimeOffset() - this.currentTimeZone();
        return this.adjustDateHours(date, offsetDiff);
    }

    /**
     * Converts and saves the date as an ISO string adjusted to the local time zone.
     * @param date - The input date string (or null)
     * @returns Adjusted ISO date string or null
     */
    saveDateToCurrentTimeZone(date: string | null): string | null {
        if (!date) {return null;}
        const adjusted = this.adjustDateHours(date, getTimeOffset() - this.currentTimeZone());
        return adjusted.toISOString();
    }

    // Example of how to convert worker's work time if needed
    // /**
    //  * Adjusts worker's work time string to the current time zone.
    //  * @param time - Time string in "HH:mm" format
    //  * @returns Adjusted time string or original if input is invalid
    //  */
    // setTimeZoneWorkTime(time: string | null | undefined): string | null | undefined {
    //     if (time) {
    //         const [hours, minutes] = time.split(':');
    //         let timeZoneHours = Number(hours) + this.currentTimeZone() - getTimeOffset();
    //         timeZoneHours = ((timeZoneHours % 24) + 24) % 24; // Normalize to 0-23
    //         return `${timeZoneHours.toString().padStart(2, '0')}:${minutes}`;
    //     }
    //     return time;
    // }
}

